import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowUpDown,
  Briefcase,
  Car,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Download,
  Folder,
  Footprints,
  Landmark,
  List,
  MapPin,
  Moon,
  MoreHorizontal,
  Pencil,
  Plane,
  Plus,
  Repeat,
  Route,
  Search,
  SlidersHorizontal,
  Star,
  Timer,
  TrainFront,
  Trash2,
  Trees,
  TrendingDown,
  TrendingUp,
  Upload,
  Utensils,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  PLAYLIST_COLLECTIONS,
  PLAYLISTS,
  STOP_COLOR,
  TREND_LABELS,
  createEmptyPlaylist,
  createStop,
  formatDistance,
  formatDuration,
  isMostUsed,
  legDistances,
  parsePlaylistImport,
  playlistsToCsv,
  waitBands,
  type Playlist,
  type PlaylistCollection,
  type PlaylistIcon,
  type PlaylistStop,
} from '../../data/playlist-mock';
import PlaylistMap, { type MapStyleName } from './PlaylistMap';
import './playlist.css';

export interface PlaylistViewProps {
  /** True while the playlists request is in flight. */
  loading?: boolean;
}

type Chip = 'all' | 'favourites' | 'most';
type Recency = 'any' | 'today' | 'week' | 'older';
type SortKey = 'last' | 'name' | 'stops' | 'distance' | 'times';
type DetailTab = 'stops' | 'usage' | 'time' | 'distance' | 'activity';
type Pane = 'list' | 'map' | 'detail';
type DialogKind = 'create' | 'edit' | 'add-stop' | 'delete' | 'collections' | null;

const ICONS: Record<PlaylistIcon, LucideIcon> = {
  list: List,
  landmark: Landmark,
  route: Route,
  train: TrainFront,
  run: Footprints,
  utensils: Utensils,
  car: Car,
  briefcase: Briefcase,
  plane: Plane,
  trees: Trees,
  moon: Moon,
};

const RECENCY_OPTIONS: Array<{ id: Recency; label: string }> = [
  { id: 'any', label: 'Any time' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'older', label: 'Older than a week' },
];

interface ToastItem {
  id: number;
  text: string;
}

function downloadCsv(filename: string, csv: string) {
  try {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch {
    /* Some browsers block the object URL. The toast still confirms the export. */
  }
}

function matchesRecency(playlist: Playlist, recency: Recency): boolean {
  if (recency === 'today') return playlist.lastUsedHours < 24;
  if (recency === 'week') return playlist.lastUsedHours <= 24 * 7;
  if (recency === 'older') return playlist.lastUsedHours > 24 * 7;
  return true;
}

function sortPlaylists(list: Playlist[], sort: SortKey): Playlist[] {
  const next = [...list];
  next.sort((a, b) => {
    if (sort === 'name') return a.name.localeCompare(b.name);
    if (sort === 'stops') return b.stops.length - a.stops.length || a.name.localeCompare(b.name);
    if (sort === 'distance') return b.distanceKm - a.distanceKm || a.name.localeCompare(b.name);
    if (sort === 'times') return b.timesUsed - a.timesUsed || a.name.localeCompare(b.name);
    return a.lastUsedHours - b.lastUsedHours || a.name.localeCompare(b.name);
  });
  return next;
}

function Skel({ className = '', ...rest }: { className?: string } & React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={`pl-skel ${className}`} {...rest} />;
}

function PlaylistSkeleton() {
  return (
    <div className="pl pl--skeleton" role="status" aria-busy="true">
      <span className="visually-hidden">Loading playlists</span>
      <div className="pl-head" data-testid="skeleton-pl-header">
        <Skel className="pl-skel--actions" />
      </div>
      <div className="pl-board">
        <div className="pl-tools" data-testid="skeleton-pl-toolbar">
          <Skel className="pl-skel--search" />
          <Skel className="pl-skel--chips" />
        </div>
        <div className="pl-rows pl-card" data-testid="skeleton-pl-list">
          <Skel className="pl-skel--row" />
          <Skel className="pl-skel--row" />
          <Skel className="pl-skel--row" />
          <Skel className="pl-skel--row" />
          <Skel className="pl-skel--row" />
        </div>
        <div className="pl-stage">
          <Skel className="pl-skel--map" data-testid="skeleton-pl-map" />
          <div className="pl-card pl-detail" data-testid="skeleton-pl-detail">
            <Skel className="pl-skel--line" />
            <div className="pl-metrics" data-testid="skeleton-pl-stats">
              <Skel className="pl-skel--stat" />
              <Skel className="pl-skel--stat" />
              <Skel className="pl-skel--stat" />
              <Skel className="pl-skel--stat" />
            </div>
            <div className="pl-detail__grid">
              <div data-testid="skeleton-pl-stops">
                <Skel className="pl-skel--row" />
                <Skel className="pl-skel--row" />
                <Skel className="pl-skel--row" />
                <Skel className="pl-skel--row" />
              </div>
              <div>
                <Skel className="pl-skel--chart" data-testid="skeleton-pl-distribution" />
                <Skel className="pl-skel--chart" data-testid="skeleton-pl-trend" />
                <div className="pl-kpis" data-testid="skeleton-pl-kpis">
                  <Skel className="pl-skel--stat" />
                  <Skel className="pl-skel--stat" />
                  <Skel className="pl-skel--stat" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyArt() {
  return (
    <svg className="pl-art" viewBox="0 0 360 230" aria-hidden="true">
      <rect className="pl-art__sky" width="360" height="230" rx="28" />
      <circle className="pl-art__sun" cx="300" cy="42" r="16" />
      <ellipse className="pl-art__cloud" cx="58" cy="52" rx="18" ry="10" />
      <ellipse className="pl-art__cloud" cx="78" cy="48" rx="14" ry="9" />
      <path className="pl-art__hill" d="M0 148c48-30 86 10 136-12 42-18 74 20 124-2 34-16 58 10 100-14v110H0z" />
      <path className="pl-art__hill pl-art__hill--front" d="M0 182c54-18 96 14 156 0 46-12 74 16 204-10v58H0z" />
      <g transform="translate(70 72)">
        <rect className="pl-art__card" x="22" y="0" width="160" height="26" rx="9" />
        <rect className="pl-art__line" x="36" y="10" width="70" height="6" rx="3" />
        <rect className="pl-art__card" x="10" y="32" width="184" height="30" rx="11" />
        <rect className="pl-art__line" x="26" y="44" width="92" height="6" rx="3" />
        <rect className="pl-art__card pl-art__card--front" x="0" y="68" width="204" height="36" rx="12" />
        <rect className="pl-art__line pl-art__line--strong" x="18" y="82" width="108" height="8" rx="4" />
      </g>
      <path className="pl-art__route" d="M78 170c36-22 64-6 102-26 30-16 58-8 96 10" />
      <g transform="translate(250 108)">
        <path className="pl-art__pin" d="M22 0C10 0 2 9 2 20c0 16 20 38 20 38s20-22 20-38C42 9 34 0 22 0z" />
        <circle className="pl-art__pin-core" cx="22" cy="20" r="7" />
      </g>
    </svg>
  );
}

function EmptyState({
  testId,
  title,
  text,
  actionLabel,
  onAction,
}: {
  testId: string;
  title: string;
  text: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <section className="pl-empty" data-testid={testId}>
      <EmptyArt />
      <h2>{title}</h2>
      <p>{text}</p>
      <button type="button" className="pl-btn pl-btn--primary" onClick={onAction}>
        {actionLabel}
      </button>
    </section>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="pl-modal" onMouseDown={onClose}>
      <div
        className="pl-modal__card"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="pl-modal__head">
          <h2>{title}</h2>
          <button type="button" className="pl-icon-btn" aria-label="Close" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Delta({ value }: { value: number }) {
  if (value === 0) return <small className="pl-delta">0%</small>;
  const up = value > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <small className={up ? 'pl-delta pl-delta--up' : 'pl-delta pl-delta--down'}>
      <Icon size={13} aria-hidden="true" />
      {up ? `+${value}%` : `${value}%`}
    </small>
  );
}

function WaitDonut({ stops }: { stops: PlaylistStop[] }) {
  const bands = waitBands(stops);
  const total = stops.length;
  const radius = 42;
  const circ = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <section className="pl-chart" data-testid="playlist-distribution" aria-label="Stop distribution">
      <h3>Stop Distribution</h3>
      <div className="pl-donut">
        <svg viewBox="0 0 120 120" aria-hidden="true">
          <circle className="pl-donut__track" cx="60" cy="60" r={radius} />
          <g transform="rotate(-90 60 60)">
            {bands.map((band) => {
              if (band.count === 0 || total === 0) return null;
              const slice = (band.count / total) * circ;
              const length = Math.max(slice - (slice > 6 ? 2 : 0), 0);
              const node = (
                <circle
                  key={band.id}
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke={band.color}
                  strokeWidth="14"
                  strokeDasharray={`${length} ${circ - length}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += slice;
              return node;
            })}
          </g>
          <text className="pl-donut__value" x="60" y="56" textAnchor="middle">{total}</text>
          <text className="pl-donut__label" x="60" y="72" textAnchor="middle">Stops</text>
        </svg>
        <ul>
          {bands.map((band) => (
            <li key={band.id}>
              <i style={{ background: band.color }} />
              <span>{band.label}</span>
              <strong>{`${band.count} (${band.percent}%)`}</strong>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function UsageTrend({ usage }: { usage: number[] }) {
  const max = Math.max(8, ...usage, 1);
  return (
    <section className="pl-chart" data-testid="playlist-trend">
      <header>
        <h3>Usage Trend (Last 30 Days)</h3>
        <span className="pl-chart__tag">Last 30 Days</span>
      </header>
      <div className="pl-trend">
        <div className="pl-trend__y" aria-hidden="true">
          <span>{max}</span>
          <span>{Math.round(max / 2)}</span>
          <span>0</span>
        </div>
        <div className="pl-trend__plot" role="img" aria-label="Playlist plays over the last 30 days">
          {usage.map((value, index) => (
            <i
              key={TREND_LABELS[index]}
              style={{ height: `${Math.max((value / max) * 100, value > 0 ? 8 : 0)}%` }}
              title={`${TREND_LABELS[index]}: ${value}`}
            />
          ))}
        </div>
      </div>
      <div className="pl-trend__x" aria-hidden="true">
        {TREND_LABELS.map((label, index) => (
          <span key={label}>{index % 10 === 0 ? label : ''}</span>
        ))}
      </div>
    </section>
  );
}

function Kpis({ playlist }: { playlist: Playlist }) {
  return (
    <div className="pl-kpis" data-testid="playlist-kpis">
      <article className="pl-kpi">
        <span className="pl-kpi__icon pl-kpi__icon--green"><Clock size={16} aria-hidden="true" /></span>
        <p>Total Mock Time</p>
        <strong>{formatDuration(playlist.mockMinutes)}</strong>
        <Delta value={playlist.mockDelta} />
      </article>
      <article className="pl-kpi">
        <span className="pl-kpi__icon pl-kpi__icon--orange"><Timer size={16} aria-hidden="true" /></span>
        <p>Avg Session Time</p>
        <strong>{formatDuration(playlist.avgSessionMinutes)}</strong>
        <Delta value={playlist.avgDelta} />
      </article>
      <article className="pl-kpi">
        <span className="pl-kpi__icon pl-kpi__icon--violet"><Clock size={16} aria-hidden="true" /></span>
        <p>Last Used</p>
        <strong>{playlist.lastUsedLabel}</strong>
        <small>{playlist.lastUsedExact}</small>
      </article>
    </div>
  );
}

const PlaylistView: React.FC<PlaylistViewProps> = ({ loading = false }) => {
  const [playlists, setPlaylists] = useState<Playlist[]>(PLAYLISTS);
  const [collections, setCollections] = useState<PlaylistCollection[]>(PLAYLIST_COLLECTIONS);
  const [selectedId, setSelectedId] = useState<string | null>(PLAYLISTS[0].id);
  const [pinnedPlaylistId, setPinnedPlaylistId] = useState<string | null>(PLAYLISTS[0].id);
  const [stopId, setStopId] = useState<string | null>(PLAYLISTS[0].stops[0].id);
  const [activeId, setActiveId] = useState<string | null>(PLAYLISTS.find((item) => item.active)?.id ?? null);
  const [chip, setChip] = useState<Chip>('all');
  const [recency, setRecency] = useState<Recency>('any');
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('last');
  const [query, setQuery] = useState('');
  const [mapQuery, setMapQuery] = useState('');
  const [mapStyle, setMapStyle] = useState<MapStyleName>('map');
  const [zoom, setZoom] = useState(1);
  const [pane, setPane] = useState<Pane>('list');
  const [detailTab, setDetailTab] = useState<DetailTab>('stops');
  const [reorder, setReorder] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [createName, setCreateName] = useState('');
  const [createCollection, setCreateCollection] = useState(PLAYLIST_COLLECTIONS[0].id);
  const [editName, setEditName] = useState('');
  const [stopName, setStopName] = useState('');
  const [stopArea, setStopArea] = useState('');
  const [stopWait, setStopWait] = useState('10');
  const [collectionDraft, setCollectionDraft] = useState('');
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const collectionRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const toastTimers = useRef<number[]>([]);
  const toastSeq = useRef(1);

  const pushToast = useCallback((text: string) => {
    const id = toastSeq.current;
    toastSeq.current += 1;
    setToasts((current) => [...current, { id, text }]);
    const timer = window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, 3200);
    toastTimers.current.push(timer);
  }, []);

  useEffect(() => () => {
    toastTimers.current.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const closeDialog = useCallback(() => setDialog(null), []);

  const counts = useMemo(() => ({
    total: playlists.length,
    favourites: playlists.filter((item) => item.favourite).length,
  }), [playlists]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = playlists.filter((playlist) => {
      if (chip === 'favourites' && !playlist.favourite) return false;
      if (chip === 'most' && !isMostUsed(playlist)) return false;
      if (!matchesRecency(playlist, recency)) return false;
      if (collectionId && playlist.collectionId !== collectionId) return false;
      if (!needle) return true;
      const collectionName = collections.find((item) => item.id === playlist.collectionId)?.name ?? '';
      const hay = [playlist.name, collectionName, ...playlist.stops.map((stop) => `${stop.name} ${stop.area}`)]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
    return sortPlaylists(filtered, sort);
  }, [chip, collectionId, collections, playlists, query, recency, sort]);

  useEffect(() => {
    if (visible.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((current) => (
      current && visible.some((item) => item.id === current) ? current : visible[0].id
    ));
  }, [visible]);

  useEffect(() => {
    if (!filtersOpen && !collectionOpen && !moreOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setFiltersOpen(false);
      setCollectionOpen(false);
      setMoreOpen(false);
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (filtersRef.current && !filtersRef.current.contains(target)) setFiltersOpen(false);
      if (collectionRef.current && !collectionRef.current.contains(target)) setCollectionOpen(false);
      if (moreRef.current && !moreRef.current.contains(target)) setMoreOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [collectionOpen, filtersOpen, moreOpen]);

  const selected = playlists.find((item) => item.id === selectedId) ?? null;

  const clearFilters = () => {
    setChip('all');
    setRecency('any');
    setCollectionId(null);
    setQuery('');
    setFiltersOpen(false);
    setCollectionOpen(false);
  };

  const openCreate = () => {
    setCreateName('');
    setCreateCollection(collections[0]?.id ?? 'city');
    setFiltersOpen(false);
    setCollectionOpen(false);
    setMoreOpen(false);
    setDialog('create');
  };

  const toggleFavourite = (id: string) => {
    const playlist = playlists.find((item) => item.id === id);
    if (!playlist) return;
    const favourite = !playlist.favourite;
    setPlaylists((current) => current.map((item) => (
      item.id === id ? { ...item, favourite } : item
    )));
    pushToast(favourite ? `Added ${playlist.name} to favourites` : `Removed ${playlist.name} from favourites`);
  };

  const selectPlaylist = (id: string) => {
    setSelectedId(id);
    setPane('detail');
    setMoreOpen(false);
  };

  if (loading) return <PlaylistSkeleton />;

  if ((selected?.id ?? null) !== pinnedPlaylistId) {
    setPinnedPlaylistId(selected?.id ?? null);
    setStopId(selected?.stops[0]?.id ?? null);
    setMapQuery('');
    setZoom(1);
    setReorder(false);
  }

  const activeCollection = collections.find((item) => item.id === collectionId) ?? null;

  const saveCreate = (event: FormEvent) => {
    event.preventDefault();
    const name = createName.trim();
    if (!name) return;
    const playlist = createEmptyPlaylist(name, createCollection);
    setPlaylists((current) => [playlist, ...current]);
    setSelectedId(playlist.id);
    setChip('all');
    setRecency('any');
    setCollectionId(null);
    setQuery('');
    setDetailTab('stops');
    setPane('detail');
    setDialog(null);
    pushToast(`Created ${name}`);
  };

  const saveEdit = (event: FormEvent) => {
    event.preventDefault();
    const name = editName.trim();
    if (!name || !selected) return;
    setPlaylists((current) => current.map((item) => (
      item.id === selected.id ? { ...item, name } : item
    )));
    setDialog(null);
    pushToast('Playlist name saved');
  };

  const saveStop = (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    const name = stopName.trim();
    if (!name) return;
    const stop = createStop(selected.id, {
      name,
      area: stopArea.trim() || 'New Delhi, Delhi, India',
      waitMinutes: Math.max(0, Number(stopWait) || 0),
      after: selected.stops[selected.stops.length - 1],
    });
    setPlaylists((current) => current.map((item) => {
      if (item.id !== selected.id) return item;
      return {
        ...item,
        stops: [...item.stops, stop],
        distanceKm: Number((item.distanceKm + 2.4).toFixed(1)),
        estimatedMinutes: item.estimatedMinutes + 8,
      };
    }));
    setStopId(stop.id);
    setDetailTab('stops');
    setDialog(null);
    pushToast(`Added ${name}`);
  };

  const confirmDelete = () => {
    if (!selected) return;
    const { id, name } = selected;
    setPlaylists((current) => current.filter((item) => item.id !== id));
    setActiveId((current) => (current === id ? null : current));
    setDialog(null);
    pushToast(`Deleted ${name}`);
  };

  const duplicatePlaylist = () => {
    if (!selected) return;
    const copy = createEmptyPlaylist(`${selected.name} copy`, selected.collectionId);
    const next: Playlist = {
      ...selected,
      id: copy.id,
      name: `${selected.name} copy`,
      active: false,
      favourite: false,
      lastUsedHours: 0,
      lastUsedLabel: 'Just now',
      lastUsedExact: copy.lastUsedExact,
      stops: selected.stops.map((stop, index) => ({ ...stop, id: `${copy.id}-${index + 1}` })),
      activity: [{ id: `${copy.id}-created`, title: 'Playlist created', time: 'Just now' }, ...selected.activity],
    };
    setPlaylists((current) => [next, ...current]);
    setSelectedId(next.id);
    setChip('all');
    setQuery('');
    setCollectionId(null);
    setRecency('any');
    setMoreOpen(false);
    setPane('detail');
    pushToast(`Duplicated ${selected.name}`);
  };

  const moveStop = (index: number, direction: -1 | 1) => {
    if (!selected) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= selected.stops.length) return;
    setPlaylists((current) => current.map((item) => {
      if (item.id !== selected.id) return item;
      const stops = [...item.stops];
      const [moved] = stops.splice(index, 1);
      stops.splice(nextIndex, 0, moved);
      return { ...item, stops };
    }));
  };

  const onImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const parsed = parsePlaylistImport(await file.text(), file.name);
      if (parsed.length === 0) {
        pushToast('No valid rows in that CSV');
        return;
      }
      setPlaylists((current) => [...parsed, ...current]);
      setSelectedId(parsed[0].id);
      setChip('all');
      setQuery('');
      setCollectionId(null);
      setRecency('any');
      setPane('detail');
      pushToast(parsed.length === 1 ? 'Imported 1 playlist' : `Imported ${parsed.length} playlists`);
    } catch {
      pushToast('Could not read that CSV');
    }
  };

  const onExport = () => {
    if (playlists.length === 0) {
      pushToast('Nothing to export');
      return;
    }
    downloadCsv('playlists.csv', playlistsToCsv(playlists));
    pushToast(playlists.length === 1 ? 'Exported 1 playlist' : `Exported ${playlists.length} playlists`);
  };

  const addCollection = (event: FormEvent) => {
    event.preventDefault();
    const name = collectionDraft.trim();
    if (!name) return;
    setCollections((current) => [...current, { id: `col-${Date.now()}`, name }]);
    setCollectionDraft('');
    pushToast(`Added collection ${name}`);
  };

  const openAddStop = () => {
    setStopName('');
    setStopArea('');
    setStopWait('10');
    setMoreOpen(false);
    setDialog('add-stop');
  };

  const renderDetail = (playlist: Playlist) => {
    const Icon = ICONS[playlist.icon];
    const active = playlist.id === activeId;
    const waiting = playlist.stops.reduce((sum, stop) => sum + stop.waitMinutes, 0);
    const moving = playlist.estimatedMinutes;
    const timeTotal = Math.max(moving + waiting, 1);
    const legs = legDistances(playlist);
    const avgWait = playlist.stops.length === 0
      ? 0
      : Math.round(waiting / playlist.stops.length);
    const showEmptyStops = playlist.stops.length === 0 && detailTab !== 'activity';

    let panel: React.ReactNode = null;
    if (detailTab === 'stops') {
      panel = (
        <ol className="pl-stops" data-testid="playlist-stops">
          {playlist.stops.map((stop, index) => (
            <li key={stop.id} className={stop.id === stopId ? 'pl-stop is-selected' : 'pl-stop'}>
              <button
                type="button"
                className="pl-stop__main"
                aria-pressed={stop.id === stopId}
                onClick={() => setStopId(stop.id)}
              >
                <span className="pl-stop__n" style={{ background: STOP_COLOR[stop.color] }}>{index + 1}</span>
                <span className="pl-stop__copy">
                  <strong>{stop.name}</strong>
                  <small>{stop.area}</small>
                </span>
                <span className="pl-stop__wait">{`Wait: ${stop.waitMinutes} min`}</span>
              </button>
              {reorder && (
                <span className="pl-stop__move">
                  <button type="button" aria-label={`Move ${stop.name} up`} disabled={index === 0} onClick={() => moveStop(index, -1)}>
                    <ChevronUp size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${stop.name} down`}
                    disabled={index === playlist.stops.length - 1}
                    onClick={() => moveStop(index, 1)}
                  >
                    <ChevronDown size={16} aria-hidden="true" />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ol>
      );
    } else if (detailTab === 'usage') {
      panel = (
        <ul className="pl-facts">
          <li><span>Times used</span><strong>{playlist.timesUsed}</strong></li>
          <li><span>Average wait</span><strong>{`${avgWait} min`}</strong></li>
          <li><span>Moving time</span><strong>{formatDuration(moving)}</strong></li>
          <li><span>Waiting time</span><strong>{formatDuration(waiting)}</strong></li>
        </ul>
      );
    } else if (detailTab === 'time') {
      panel = (
        <div className="pl-panel">
          <div className="pl-bar">
            <div className="pl-bar__row"><span>Moving</span><strong>{formatDuration(moving)}</strong></div>
            <div className="pl-bar__track"><i style={{ width: `${(moving / timeTotal) * 100}%` }} /></div>
          </div>
          <div className="pl-bar">
            <div className="pl-bar__row"><span>Waiting at stops</span><strong>{formatDuration(waiting)}</strong></div>
            <div className="pl-bar__track"><i style={{ width: `${(waiting / timeTotal) * 100}%` }} /></div>
          </div>
        </div>
      );
    } else if (detailTab === 'distance') {
      panel = legs.length === 0 ? (
        <p className="pl-note">Add at least two stops to see the distance between them.</p>
      ) : (
        <ol className="pl-legs">
          {legs.map((leg) => (
            <li key={`${leg.from}-${leg.to}`}>
              <span>{`${leg.from} → ${leg.to}`}</span>
              <strong>{formatDistance(leg.km)}</strong>
            </li>
          ))}
        </ol>
      );
    } else {
      panel = (
        <ol className="pl-activity">
          {playlist.activity.map((item) => (
            <li key={item.id}>
              <span />
              <div>
                <strong>{item.title}</strong>
                <small>{item.time}</small>
              </div>
            </li>
          ))}
        </ol>
      );
    }

    return (
      <article className="pl-detail" data-testid="playlist-detail">
        <div className="pl-detail__head">
          <div className="pl-detail__id">
            <span className={`pl-ico pl-tone--${playlist.tone}`} aria-hidden="true">
              <Icon size={18} />
            </span>
            <div>
              <div className="pl-titleline">
                <h2>{playlist.name}</h2>
                <button
                  type="button"
                  className={playlist.favourite ? 'pl-star is-on' : 'pl-star'}
                  aria-pressed={playlist.favourite}
                  aria-label={playlist.favourite ? `Remove ${playlist.name} from favourites` : `Add ${playlist.name} to favourites`}
                  onClick={() => toggleFavourite(playlist.id)}
                >
                  <Star size={16} fill={playlist.favourite ? 'currentColor' : 'none'} aria-hidden="true" />
                </button>
                {active && (
                  <span className="pl-live">
                    <i />
                    Active
                  </span>
                )}
              </div>
              <p className="pl-detail__sub">
                {`${playlist.stops.length} stops · Created on ${playlist.createdLabel} · Last used: ${playlist.lastUsedLabel}`}
              </p>
            </div>
          </div>
          <div className="pl-detail__actions">
            <button
              type="button"
              className="pl-btn pl-btn--ghost pl-btn--small"
              onClick={() => {
                setEditName(playlist.name);
                setMoreOpen(false);
                setDialog('edit');
              }}
            >
              <Pencil size={14} aria-hidden="true" />
              Edit
            </button>
            <div className="pl-more" ref={moreRef}>
              <button
                type="button"
                className="pl-btn pl-btn--ghost pl-btn--small"
                aria-label="More actions"
                aria-expanded={moreOpen}
                aria-haspopup="menu"
                onClick={() => setMoreOpen((open) => !open)}
              >
                <MoreHorizontal size={16} aria-hidden="true" />
                More
              </button>
              {moreOpen && (
                <div className="pl-menu" role="menu" aria-label={`Actions for ${playlist.name}`}>
                  <button type="button" role="menuitem" onClick={duplicatePlaylist}>
                    <Copy size={15} aria-hidden="true" />
                    Duplicate
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setActiveId((current) => (current === playlist.id ? null : playlist.id));
                      setMoreOpen(false);
                      pushToast(active ? 'Cleared active playlist' : `${playlist.name} is active`);
                    }}
                  >
                    <Repeat size={15} aria-hidden="true" />
                    {active ? 'Clear active' : 'Set as active'}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="is-danger"
                    onClick={() => {
                      setMoreOpen(false);
                      setDialog('delete');
                    }}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                    Delete playlist
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="pl-metrics" data-testid="playlist-stats">
          <div className="pl-metric">
            <span className="pl-metric__icon pl-metric__icon--rose"><MapPin size={16} aria-hidden="true" /></span>
            <span><small>Total Distance</small><strong>{formatDistance(playlist.distanceKm)}</strong></span>
          </div>
          <div className="pl-metric">
            <span className="pl-metric__icon pl-metric__icon--violet"><Clock size={16} aria-hidden="true" /></span>
            <span><small>Estimated Time</small><strong>{formatDuration(playlist.estimatedMinutes)}</strong></span>
          </div>
          <div className="pl-metric">
            <span className="pl-metric__icon pl-metric__icon--blue"><Repeat size={16} aria-hidden="true" /></span>
            <span><small>Times Used</small><strong>{playlist.timesUsed === 1 ? '1 time' : `${playlist.timesUsed} times`}</strong></span>
          </div>
          <div className="pl-metric">
            <span className="pl-metric__icon pl-metric__icon--green"><MapPin size={16} aria-hidden="true" /></span>
            <span><small>Total Stops</small><strong>{playlist.stops.length}</strong></span>
          </div>
        </div>

        <div className="pl-tabs-row">
          <div className="pl-tabs" role="tablist" aria-label="Playlist details">
            {([
              ['stops', `Stops (${playlist.stops.length})`],
              ['usage', 'Usage Stats'],
              ['time', 'Time Analysis'],
              ['distance', 'Distance Analysis'],
              ['activity', 'Recent Activity'],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={detailTab === id}
                onClick={() => setDetailTab(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="pl-tabs__actions">
            {detailTab === 'stops' && playlist.stops.length > 1 && (
              <button type="button" className="pl-btn pl-btn--ghost pl-btn--small" onClick={() => setReorder((current) => !current)}>
                <ArrowUpDown size={14} aria-hidden="true" />
                {reorder ? 'Done' : 'Reorder'}
              </button>
            )}
            <button type="button" className="pl-btn pl-btn--primary pl-btn--small" onClick={openAddStop}>
              <Plus size={14} aria-hidden="true" />
              Add Stop
            </button>
          </div>
        </div>

        <div className="pl-detail__body">
        {showEmptyStops ? (
          <EmptyState
            testId="playlist-stops-empty"
            title="No stops yet"
            text="Add the first stop and it will show on the illustrated map."
            actionLabel="Add Stop"
            onAction={openAddStop}
          />
        ) : (
          <div className="pl-detail__grid">
            <div className="pl-detail__main">{panel}</div>
            <aside className="pl-insights">
              <WaitDonut stops={playlist.stops} />
              <UsageTrend usage={playlist.usage} />
              <Kpis playlist={playlist} />
            </aside>
          </div>
        )}
        </div>
      </article>
    );
  };

  return (
    <div className="pl" data-pane={pane}>
      <header className="pl-head">
        <div className="pl-head__actions">
          <button type="button" className="pl-btn pl-btn--primary" onClick={openCreate}>
            <Plus size={16} aria-hidden="true" />
            Create Playlist
          </button>
          <button type="button" className="pl-btn pl-btn--ghost" onClick={() => fileRef.current?.click()}>
            <Upload size={16} aria-hidden="true" />
            Import (CSV)
          </button>
          <button type="button" className="pl-btn pl-btn--ghost" onClick={onExport} disabled={playlists.length === 0}>
            <Download size={16} aria-hidden="true" />
            Export (CSV)
          </button>
          <button
            type="button"
            className="pl-btn pl-btn--ghost"
            aria-label="Manage collections"
            onClick={() => {
              setCollectionDraft('');
              setDialog('collections');
            }}
          >
            <Folder size={16} aria-hidden="true" />
            Collections
          </button>
        </div>
      </header>

      {playlists.length === 0 ? (
        <EmptyState
          testId="playlist-empty"
          title="No playlists yet"
          text="Create a playlist, then add stops. The map, timing, and charts fill in from that list."
          actionLabel="Create Playlist"
          onAction={openCreate}
        />
      ) : (
        <>
          {visible.length > 0 && (
            <div className="pl-switch" role="tablist" aria-label="Playlist panes">
              {([
                ['list', 'List', 'Show playlist list'],
                ['map', 'Map', 'Show playlist map'],
                ['detail', 'Details', 'Show playlist details'],
              ] as const).map(([id, label, name]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-label={name}
                  aria-selected={pane === id}
                  onClick={() => setPane(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className={`pl-board${visible.length === 0 ? ' is-empty' : ''}`}>
            <div className="pl-tools">
              <div className="pl-searchrow">
                <label className="pl-search">
                  <Search size={16} aria-hidden="true" />
                  <span className="visually-hidden">Search playlists</span>
                  <input
                    type="search"
                    value={query}
                    placeholder="Search playlists..."
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </label>
                <div className="pl-filters" ref={filtersRef}>
                  <button
                    type="button"
                    className={`pl-btn pl-btn--ghost${recency !== 'any' ? ' is-on' : ''}`}
                    aria-expanded={filtersOpen}
                    onClick={() => {
                      setCollectionOpen(false);
                      setFiltersOpen((open) => !open);
                    }}
                  >
                    <SlidersHorizontal size={16} aria-hidden="true" />
                    Filters
                  </button>
                  {filtersOpen && (
                    <div className="pl-pop pl-pop--filters" role="dialog" aria-label="Filters">
                      <p>Last used</p>
                      {RECENCY_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          aria-pressed={recency === option.id}
                          onClick={() => {
                            setRecency(option.id);
                            setFiltersOpen(false);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="pl-chips" role="group" aria-label="Playlist filters">
                <button type="button" className="pl-chip" aria-pressed={chip === 'all'} onClick={() => setChip('all')}>
                  {`All (${counts.total})`}
                </button>
                <button type="button" className="pl-chip" aria-pressed={chip === 'favourites'} onClick={() => setChip('favourites')}>
                  <Star size={14} aria-hidden="true" fill={chip === 'favourites' ? 'currentColor' : 'none'} />
                  {`Favourites (${counts.favourites})`}
                </button>
                <button type="button" className="pl-chip" aria-pressed={chip === 'most'} onClick={() => setChip('most')}>
                  Most Used
                </button>
                <div className="pl-collection" ref={collectionRef}>
                  <button
                    type="button"
                    className={`pl-chip${collectionId ? ' is-on' : ''}`}
                    aria-expanded={collectionOpen}
                    aria-haspopup="menu"
                    aria-label={activeCollection ? `Collection ${activeCollection.name}` : 'Filter by collection'}
                    onClick={() => {
                      setFiltersOpen(false);
                      setCollectionOpen((open) => !open);
                    }}
                  >
                    <Folder size={14} aria-hidden="true" />
                    {activeCollection ? activeCollection.name : 'Collections'}
                    <ChevronDown size={14} aria-hidden="true" />
                  </button>
                  {collectionOpen && (
                    <div className="pl-pop pl-pop--collections" role="menu" aria-label="Collections">
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={collectionId == null}
                        onClick={() => {
                          setCollectionId(null);
                          setCollectionOpen(false);
                        }}
                      >
                        All collections
                      </button>
                      {collections.map((collection) => (
                        <button
                          key={collection.id}
                          type="button"
                          role="menuitemradio"
                          aria-checked={collectionId === collection.id}
                          onClick={() => {
                            setCollectionId(collection.id);
                            setCollectionOpen(false);
                          }}
                        >
                          {collection.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <label className="pl-sort">
                Sort by:
                <select aria-label="Sort playlists" value={sort} onChange={(event) => setSort(event.target.value as SortKey)}>
                  <option value="last">Last Used</option>
                  <option value="name">Name</option>
                  <option value="stops">Stops</option>
                  <option value="distance">Distance</option>
                  <option value="times">Times Used</option>
                </select>
              </label>
            </div>

            {visible.length === 0 ? (
              <EmptyState
                testId="playlist-empty"
                title="No playlists match"
                text="Try another name, filter, or collection."
                actionLabel="Clear filters"
                onAction={clearFilters}
              />
            ) : (
              <>
                <div className="pl-rows pl-card" data-testid="playlist-list" role="list">
                  {visible.map((playlist) => {
                    const Icon = ICONS[playlist.icon];
                    const current = playlist.id === selectedId;
                    return (
                      <div key={playlist.id} className={current ? 'pl-row is-selected' : 'pl-row'} role="listitem">
                        <button
                          type="button"
                          className="pl-row__open"
                          aria-current={current ? 'true' : undefined}
                          aria-label={`${playlist.name}, ${playlist.stops.length} stops, last used ${playlist.lastUsedLabel}`}
                          onClick={() => selectPlaylist(playlist.id)}
                        >
                          <span className={`pl-ico pl-tone--${playlist.tone}`} aria-hidden="true">
                            <Icon size={18} />
                          </span>
                          <span className="pl-row__copy">
                            <strong className="pl-row__name">{playlist.name}</strong>
                            <span className="pl-row__sub">{`${playlist.stops.length} stops · Last used: ${playlist.lastUsedLabel}`}</span>
                            <span className="pl-row__meta">
                              <span><MapPin size={13} aria-hidden="true" />{formatDistance(playlist.distanceKm)}</span>
                              <span><Clock size={13} aria-hidden="true" />{formatDuration(playlist.estimatedMinutes)}</span>
                              <span>
                                <Repeat size={13} aria-hidden="true" />
                                {playlist.timesUsed === 1 ? '1 time' : `${playlist.timesUsed} times`}
                              </span>
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          className={playlist.favourite ? 'pl-star is-on' : 'pl-star'}
                          aria-pressed={playlist.favourite}
                          aria-label={playlist.favourite ? `Remove ${playlist.name} from favourites` : `Add ${playlist.name} to favourites`}
                          onClick={() => toggleFavourite(playlist.id)}
                        >
                          <Star size={16} fill={playlist.favourite ? 'currentColor' : 'none'} aria-hidden="true" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="pl-stage">
                  <PlaylistMap
                    stops={selected?.stops ?? []}
                    selectedStopId={stopId}
                    styleName={mapStyle}
                    query={mapQuery}
                    zoom={zoom}
                    onSelectStop={(id) => {
                      setStopId(id);
                      setDetailTab('stops');
                    }}
                    onStyle={setMapStyle}
                    onQuery={setMapQuery}
                    onZoom={setZoom}
                  />
                  {selected && renderDetail(selected)}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {dialog === 'create' && (
        <Modal title="Create playlist" onClose={closeDialog}>
          <form onSubmit={saveCreate}>
            <label className="pl-field">
              <span>Playlist name</span>
              <input value={createName} onChange={(event) => setCreateName(event.target.value)} required maxLength={80} autoFocus />
            </label>
            <label className="pl-field">
              <span>Collection</span>
              <select value={createCollection} onChange={(event) => setCreateCollection(event.target.value)}>
                {collections.map((collection) => (
                  <option key={collection.id} value={collection.id}>{collection.name}</option>
                ))}
              </select>
            </label>
            <div className="pl-dialog__actions">
              <button type="button" className="pl-btn pl-btn--ghost" onClick={closeDialog}>Cancel</button>
              <button type="submit" className="pl-btn pl-btn--primary">Save playlist</button>
            </div>
          </form>
        </Modal>
      )}

      {dialog === 'edit' && selected && (
        <Modal title="Edit playlist" onClose={closeDialog}>
          <form onSubmit={saveEdit}>
            <label className="pl-field">
              <span>Playlist name</span>
              <input value={editName} onChange={(event) => setEditName(event.target.value)} required maxLength={80} autoFocus />
            </label>
            <div className="pl-dialog__actions">
              <button type="button" className="pl-btn pl-btn--ghost" onClick={closeDialog}>Cancel</button>
              <button type="submit" className="pl-btn pl-btn--primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {dialog === 'add-stop' && (
        <Modal title="Add stop" onClose={closeDialog}>
          <form onSubmit={saveStop}>
            <label className="pl-field">
              <span>Stop name</span>
              <input value={stopName} onChange={(event) => setStopName(event.target.value)} required maxLength={80} autoFocus />
            </label>
            <label className="pl-field">
              <span>Area</span>
              <input
                value={stopArea}
                placeholder="New Delhi, Delhi, India"
                onChange={(event) => setStopArea(event.target.value)}
                maxLength={120}
              />
            </label>
            <label className="pl-field">
              <span>Wait minutes</span>
              <input
                type="number"
                min={0}
                max={240}
                value={stopWait}
                onChange={(event) => setStopWait(event.target.value)}
              />
            </label>
            <div className="pl-dialog__actions">
              <button type="button" className="pl-btn pl-btn--ghost" onClick={closeDialog}>Cancel</button>
              <button type="submit" className="pl-btn pl-btn--primary">Add stop</button>
            </div>
          </form>
        </Modal>
      )}

      {dialog === 'delete' && selected && (
        <Modal title="Delete playlist" onClose={closeDialog}>
          <p className="pl-dialog__lead">{`Delete “${selected.name}” from this list?`}</p>
          <div className="pl-dialog__actions">
            <button type="button" className="pl-btn pl-btn--ghost" onClick={closeDialog} autoFocus>Cancel</button>
            <button type="button" className="pl-btn pl-btn--danger" onClick={confirmDelete}>Delete</button>
          </div>
        </Modal>
      )}

      {dialog === 'collections' && (
        <Modal title="Collections" onClose={closeDialog}>
          <ul className="pl-collections">
            {collections.map((collection) => {
              const count = playlists.filter((item) => item.collectionId === collection.id).length;
              return (
                <li key={collection.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setCollectionId(collection.id);
                      setChip('all');
                      setDialog(null);
                    }}
                  >
                    <strong>{collection.name}</strong>
                    <small>{count === 1 ? '1 playlist' : `${count} playlists`}</small>
                  </button>
                </li>
              );
            })}
          </ul>
          <form onSubmit={addCollection}>
            <label className="pl-field">
              <span>New collection</span>
              <input value={collectionDraft} onChange={(event) => setCollectionDraft(event.target.value)} maxLength={40} />
            </label>
            <div className="pl-dialog__actions">
              <button type="submit" className="pl-btn pl-btn--primary">Add collection</button>
            </div>
          </form>
        </Modal>
      )}

      <input
        ref={fileRef}
        className="pl-file"
        type="file"
        accept=".csv,text/csv"
        tabIndex={-1}
        aria-hidden="true"
        onChange={onImport}
      />

      <div className="pl-toasts" aria-live="polite">
        {toasts.map((toast) => (
          <p key={toast.id} className="pl-toast">{toast.text}</p>
        ))}
      </div>
    </div>
  );
};

export default PlaylistView;
